import shortid from "shortid";

import Service from "lib/models/Service";

import {
  Pdf,
  CaseTypeSource,
  RfeImageSource,
  BlockSource,
  CorrectionSource,
  BlockInfoSource,
  CaseType,
  RfeImage,
  Block,
  Correction,
  EntityType,
  EntityTypeSource,
  PdfSource,
} from "./models";
import RfeUploadForm from "./commands/RfeUploadForm";
import RfeEditForm from "./commands/RfeEditForm";
import BlockForm from "./commands/BlockForm";

export default class RfeService extends Service {
  public async findCategories(form: RfeEditForm): Promise<string[][]> {
    try {
      const res = await this.get("/rfe/categories", {
        base: form.base,
        case_name: form.caseName,
      });
      if (!res) {
        throw res;
      }
      return res;
    } catch (e) {
      throw e;
    }
  }

  public async findPdfs(): Promise<Array<{ key: string; list: Pdf[] }>> {
    try {
      const res = await this.get("/rfe/pdfs");
      if (!res) {
        throw res;
      }
      return Object.keys(res).map((key) => {
        const list = res[key]
          .map((x: PdfSource) => new Pdf(x.id, x))
          .sort((a: Pdf, b: Pdf) => {
            if (a.file > b.file) {
              return 1;
            }
            if (a.file < b.file) {
              return -1;
            }
            return 0;
          })
          .sort((a: Pdf, b: Pdf) => b.progressStatus - a.progressStatus)
          .sort((_: any, b: Pdf) => {
            if (b.progressStatus === 2) {
              return -1;
            }
            return 0;
          });
        return {
          key,
          list,
        };
      });
    } catch (e) {
      throw e;
    }
  }

  public async findCaseTypes(): Promise<CaseType[] | void> {
    try {
      const res = await this.get("/rfe/types");
      if (!res) {
        throw res;
      }
      return res.map(
        (x: CaseTypeSource) => new CaseType(shortid.generate(), x),
      );
    } catch (e) {
      throw e;
    }
  }

  public async findImages(pdfId: number): Promise<RfeImage[]> {
    try {
      const res = await this.get(`/rfe/pdfs/${pdfId}/images`);
      if (!res) {
        throw res.map((x: RfeImageSource) => ({
          name: x.file.split("/")[x.file.split("/").length - 1],
          ...x,
        }));
      }
      return res.map((x: RfeImageSource) => new RfeImage(x.id, x));
    } catch (e) {
      throw e;
    }
  }

  public async findBlocks(imageId: number): Promise<Block[]> {
    try {
      const res = await this.get(`/rfe/images/${imageId}/blocks`);
      if (!res) {
        throw res;
      }
      return res.map((x: BlockSource) => new Block(x.id, x));
    } catch (e) {
      throw e;
    }
  }

  public async findCorrections(blockId: number): Promise<Correction[]> {
    try {
      const res = await this.get(`/rfe/blocks/${blockId}/corrections`);
      if (!res) {
        throw res;
      }
      return res.map(
        (x: CorrectionSource) => new Correction(shortid.generate(), x),
      );
    } catch (e) {
      throw e;
    }
  }

  public async findEntityTypes(): Promise<string[]> {
    try {
      const res = await this.get(`/rfe/entity_types`);
      if (!res) {
        throw res;
      }
      return res;
    } catch (e) {
      throw e;
    }
  }

  public async findEntities(blockId: number): Promise<EntityType[]> {
    try {
      const res = await this.get(`/rfe/blocks/${blockId}/entities`);
      if (!res) {
        throw res;
      }
      return res.map(
        (x: EntityTypeSource) => new EntityType(shortid.generate(), x),
      );
    } catch (e) {
      throw e;
    }
  }

  public async getBlock(blockId: number): Promise<BlockInfoSource> {
    try {
      const res: BlockInfoSource = await this.get(`/rfe/blocks/${blockId}`);
      if (!res) {
        throw res;
      }
      return res;
    } catch (e) {
      throw e;
    }
  }

  public async checkPdfDuplicated(file: File): Promise<number | undefined> {
    try {
      const res = await this.get("/rfe/pdf", {
        filename: file.name,
        filesize: file.size,
      });
      if (!res) {
        throw res;
      }
      return res.id;
    } catch (e) {
      return e;
    }
  }

  public async filePdfUpload(
    form: RfeUploadForm,
  ): Promise<{ id: number; file: string }> {
    try {
      const res = await this.postWithFormData("/rfe/pdfs", form.data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (!res) {
        throw res;
      }
      return res;
    } catch (e) {
      throw e;
    }
  }

  public async updateImage(imageId: number): Promise<boolean> {
    try {
      await this.put(`/rfe/images/${imageId}`);
      return true;
    } catch (e) {
      throw e;
    }
  }

  public async updateBlock(blockId: number, form: BlockForm): Promise<boolean> {
    try {
      if (form.touched.category || form.touched.feedback) {
        const res = await this.put(`/rfe/blocks/${blockId}`, {
          category: form.category,
          feedback: form.feedback,
        });
        if (!res) {
          throw res;
        }
      }
      if (form.corrections && form.corrections.length > 0) {
        const corrections = form.corrections.map((x) =>
          this.addCorrection(blockId, x),
        );
        const addCorrectionRes = await Promise.all(corrections);
        if (!addCorrectionRes) {
          throw addCorrectionRes;
        }
      }
      if (form.entities && form.entities.length > 0) {
        const entities = form.entities.map((x) =>
          this.addEntityType(blockId, x),
        );
        const addEntityTypeRes = await Promise.all(entities);
        if (!addEntityTypeRes) {
          throw addEntityTypeRes;
        }
      }
      return true;
    } catch (e) {
      throw e;
    }
  }

  public async addCorrection(blockId: number, body: Correction) {
    try {
      const res = await this.post(`/rfe/blocks/${blockId}/corrections`, {
        before: body.before,
        after: body.after,
        arrayIdx: body.arrayIdx,
      });
      if (!res) {
        throw res;
      }
      return true;
    } catch (e) {
      throw e;
    }
  }

  public async addEntityType(blockId: number, body: EntityType) {
    try {
      const res = await this.post(`/rfe/blocks/${blockId}/entities`, {
        entity: body.entity,
        entityType: body.entityType,
      });
      if (!res) {
        throw res;
      }
      return true;
    } catch (e) {
      throw e;
    }
  }
}
